import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { AppMessageService } from '../../../app/services/Messageservice';
export interface ToastMessage {
  /** Unique id, auto-generated — used internally to remove the toast after its life expires */
  id: number;
  severity: string;
  summary?: string;
  detail?: string;
  /** Duration in ms before auto-dismiss. Defaults to 3000 if not provided. */
  life?: number;
}

/**
 * Drop-in replacement for PrimeNG's p-toast.
 *
 * MIGRATION: only the tag name changes.
 *   <p-toast position="top-right" [life]="4000" />
 *   becomes
 *   <app-toast position="top-right" [life]="4000" />
 *
 * The [life] input here sets a *default* life for toasts that don't specify
 * their own `life` — matching PrimeNG's behavior where messageService.add({life})
 * overrides the component-level default.
 */
@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="app-toast-container" [ngClass]="'app-toast-' + position">
      @for (toast of toasts; track toast.id) {
        <div class="app-toast" [ngClass]="'app-toast-' + toast.severity">
          <div class="app-toast-content">
            @if (toast.summary) {
              <div class="app-toast-summary">{{ toast.summary }}</div>
            }
            @if (toast.detail) {
              <div class="app-toast-detail">{{ toast.detail }}</div>
            }
          </div>
          <button
            type="button"
            class="app-toast-close"
            aria-label="Close"
            (click)="removeToast(toast.id)"
          >
            &times;
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .app-toast-container {
        position: fixed;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-width: 460px;
        width: 100%;
        pointer-events: none;
      }

      .app-toast-top-right {
        top: 20px;
        right: 20px;
      }

      .app-toast-top-left {
        top: 20px;
        left: 20px;
      }

      .app-toast-bottom-right {
        bottom: 20px;
        right: 20px;
      }

      .app-toast-bottom-left {
        bottom: 20px;
        left: 20px;
      }

      .app-toast-top-center {
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
      }

      .app-toast-bottom-center {
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
      }

      .app-toast {
        pointer-events: auto;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        padding: 14px 16px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        background: #ffffff;
        border-left: 4px solid #6c757d;
        animation: app-toast-slide-in 0.25s ease-out;
        font-family:
          -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
          Arial, sans-serif;
      }

      @keyframes app-toast-slide-in {
        from {
          opacity: 0;
          transform: translateY(-8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Severity variants */
      .app-toast-success {
        border-left-color: #22c55e;
        background: #f0fdf4;
      }

      .app-toast-error {
        border-left-color: #ef4444;
        background: #fef2f2;
      }

      .app-toast-warn {
        border-left-color: #f59e0b;
        background: #fffbeb;
      }

      .app-toast-info,
      .app-toast-default {
        border-left-color: #3b82f6;
        background: #eff6ff;
      }

      .app-toast-content {
        flex: 1;
        min-width: 0;
      }

      .app-toast-summary {
        font-size: 18px;
        font-weight: 700;
        line-height: 1.4;
        color: #1f2937;
        margin: 0 0 4px 0;
      }

      .app-toast-detail {
        font-size: 16px;
        font-weight: 400;
        line-height: 1.5;
        color: #4b5563;
        margin: 0;
        word-break: break-word;
      }

      .app-toast-success .app-toast-summary {
        color: #15803d;
      }
      .app-toast-error .app-toast-summary {
        color: #b91c1c;
      }
      .app-toast-warn .app-toast-summary {
        color: #b45309;
      }
      .app-toast-info .app-toast-summary,
      .app-toast-default .app-toast-summary {
        color: #1d4ed8;
      }

      .app-toast-close {
        background: transparent;
        border: none;
        cursor: pointer;
        font-size: 28px;
        line-height: 1;
        color: #9ca3af;
        padding: 0;
        margin-left: 8px;
        flex-shrink: 0;
      }

      .app-toast-close:hover {
        color: #374151;
      }
    `,
  ],
})
export class AppToastComponent implements OnInit, OnDestroy {
  /** Matches p-toast's position input: "top-right" | "top-left" | "bottom-right" | "bottom-left" | "top-center" | "bottom-center" */
  @Input() position: string = 'top-right';

  /** Matches p-toast's [life] input — accepted for API compatibility. Per-toast life (set via messageService.add({life})) always takes priority; this is only used as the fallback default inside AppMessageService. */
  @Input() life: number = 3000;

  toasts: ToastMessage[] = [];
  private sub?: Subscription;

  constructor(private readonly messageService: AppMessageService) {}

  ngOnInit(): void {
    this.sub = this.messageService.messages$.subscribe((toasts) => {
      this.toasts = toasts;
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  removeToast(id: number): void {
    this.messageService.removeMessage(id);
  }
}
